// src/circles.ts
// Private Circles: small, members-only groups led by a quartermaster.
//
// Data (checked by firestore.rules):
// - circles/{cid}: name, purpose, guidelines, status ('pending' until an admin
//   approves it in the Firebase console, then 'approved'; 'paused' when nobody
//   is left to lead), quartermaster (uid), quartermasterName, mates (up to 2
//   first-mate uids), createdBy, createdAt
// - circles/{cid}/members/{uid}: username, joinedAt
// - circles/{cid}/joinRequests/{uid}: username, note, createdAt
// - circles/{cid}/posts/{pid} and .../replies/{rid}: like room posts and
//   comments, plus `hidden` (set by a lead). Anonymous authors are kept in
//   roomAuthors/{cid_pid} or {cid_pid_rid}.
// - users/{uid}/private/rooms also lists your circles, circle posts, replies,
//   and join requests, so Delete my account and Download my data find them.
import { useEffect, useState } from "react";
import {
  Timestamp,
  arrayRemove,
  arrayUnion,
  collection,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
  updateDoc,
  writeBatch,
} from "firebase/firestore";
import { db } from "./firebase";
import { checkPace, markPace } from "./pace";
import { LIMITS } from "./rooms";
import { emailCircleRequest } from "./reportEmail";

export type Circle = {
  id: string;
  name: string;
  purpose: string;
  guidelines: string;
  status: "pending" | "approved" | "paused";
  quartermaster: string;
  quartermasterName: string;
  mates: string[];
  createdAt?: Timestamp;
};
export type Member = { id: string; username: string; joinedAt?: Timestamp };
export type JoinRequest = { id: string; username: string; note?: string; createdAt?: Timestamp };

export const CIRCLE_LIMITS = { name: 60, purpose: 500, guidelines: 2000, note: 300 };

export const circleRef = (cid: string) => doc(db, "circles", cid);
export const membersCol = (cid: string) => collection(db, "circles", cid, "members");
export const requestsCol = (cid: string) => collection(db, "circles", cid, "joinRequests");
export const circlePostsCol = (cid: string) => collection(db, "circles", cid, "posts");
export const circlePostRef = (cid: string, pid: string) => doc(db, "circles", cid, "posts", pid);
export const circleRepliesCol = (cid: string, pid: string) =>
  collection(db, "circles", cid, "posts", pid, "replies");
const authorRef = (key: string) => doc(db, "roomAuthors", key);
const mineRef = (uid: string) => doc(db, "users", uid, "private", "rooms");

export const isLead = (c: Circle | null | undefined, uid?: string) =>
  !!c && !!uid && (c.quartermaster === uid || (c.mates || []).includes(uid));

// ---------- starting and joining ----------

export async function requestCircle(
  uid: string,
  username: string,
  name: string,
  purpose: string,
  guidelines: string
) {
  const ref = doc(collection(db, "circles"));
  const batch = writeBatch(db);
  const c = {
    name: name.trim().slice(0, CIRCLE_LIMITS.name),
    purpose: purpose.trim().slice(0, CIRCLE_LIMITS.purpose),
    guidelines: guidelines.trim().slice(0, CIRCLE_LIMITS.guidelines),
  };
  batch.set(ref, {
    ...c,
    status: "pending",
    quartermaster: uid,
    quartermasterName: username,
    mates: [],
    createdBy: uid,
    createdAt: serverTimestamp(),
  });
  batch.set(doc(membersCol(ref.id), uid), { username, joinedAt: serverTimestamp() });
  await batch.commit();
  await setDoc(mineRef(uid), { circles: arrayUnion(ref.id) }, { merge: true });
  // Let the moderators know there's a circle to approve. (It's saved either way.)
  emailCircleRequest({ id: ref.id, ...c, uid, username }).catch((err) =>
    console.error("Circle request email failed", err)
  );
  return ref.id;
}

export async function askToJoin(uid: string, username: string, cid: string, note: string) {
  await setDoc(doc(requestsCol(cid), uid), {
    username,
    note: note.trim().slice(0, CIRCLE_LIMITS.note),
    createdAt: serverTimestamp(),
  });
  await setDoc(mineRef(uid), { circleRequests: arrayUnion(cid) }, { merge: true });
}

export async function cancelRequest(uid: string, cid: string) {
  const batch = writeBatch(db);
  batch.delete(doc(requestsCol(cid), uid));
  await batch.commit();
  await setDoc(mineRef(uid), { circleRequests: arrayRemove(cid) }, { merge: true });
}

// A lead lets someone in (or turns them away).
export async function approveRequest(cid: string, r: JoinRequest) {
  const batch = writeBatch(db);
  batch.set(doc(membersCol(cid), r.id), { username: r.username, joinedAt: serverTimestamp() });
  batch.delete(doc(requestsCol(cid), r.id));
  await batch.commit();
}
export async function declineRequest(cid: string, uid: string) {
  const batch = writeBatch(db);
  batch.delete(doc(requestsCol(cid), uid));
  await batch.commit();
}

// After you're let in, remember the circle as yours (for delete/download).
export async function rememberCircle(uid: string, cid: string) {
  await setDoc(mineRef(uid), { circles: arrayUnion(cid), circleRequests: arrayRemove(cid) }, { merge: true });
}

// ---------- the quartermaster's tools ----------

export async function editCircle(cid: string, purpose: string, guidelines: string) {
  await updateDoc(circleRef(cid), {
    purpose: purpose.trim().slice(0, CIRCLE_LIMITS.purpose),
    guidelines: guidelines.trim().slice(0, CIRCLE_LIMITS.guidelines),
  });
}

export async function setMates(cid: string, mates: string[]) {
  await updateDoc(circleRef(cid), { mates: mates.slice(0, 2) });
}

export async function passRole(c: Circle, to: Member) {
  await updateDoc(circleRef(c.id), {
    quartermaster: to.id,
    quartermasterName: to.username,
    mates: (c.mates || []).filter((m) => m !== to.id),
  });
}

export async function removeMember(c: Circle, uid: string) {
  const batch = writeBatch(db);
  batch.delete(doc(membersCol(c.id), uid));
  await batch.commit();
  // a removed first mate stops being one
  if (c.mates?.includes(uid)) await setMates(c.id, c.mates.filter((m) => m !== uid)).catch(() => undefined);
}

// Leaving. A quartermaster hands the circle to a first mate first; with no
// first mate, the circle pauses until a moderator picks a new quartermaster.
export async function leaveCircle(c: Circle, uid: string) {
  if (c.quartermaster === uid) {
    const nextId = (c.mates || [])[0];
    const next = nextId ? await getDoc(doc(membersCol(c.id), nextId)) : null;
    if (next?.exists()) {
      await passRole(c, { id: next.id, username: next.data().username });
    } else {
      await updateDoc(circleRef(c.id), { status: "paused" });
    }
  }
  await writeBatch(db).delete(doc(membersCol(c.id), uid)).commit();
  await setDoc(mineRef(uid), { circles: arrayRemove(c.id) }, { merge: true });
}

// ---------- posts and comments ----------

const byline = (username: string, anonymous: boolean) =>
  anonymous ? { anonymous: true, username: "anonymous" } : { anonymous: false, username };

export async function startCirclePost(
  uid: string,
  username: string,
  cid: string,
  p: { subject: string; message: string; color: string; cw: string; anonymous: boolean }
) {
  const ref = doc(circlePostsCol(cid));
  await checkPace(uid, "post");
  const batch = writeBatch(db);
  markPace(batch, uid, "post");
  if (p.anonymous) batch.set(authorRef(`${cid}_${ref.id}`), { uid });
  batch.set(ref, {
    title: p.subject.trim().slice(0, LIMITS.title),
    body: p.message.slice(0, LIMITS.body),
    color: p.color,
    ...(p.cw ? { cw: p.cw.slice(0, LIMITS.cw) } : {}),
    ...byline(username, p.anonymous),
    ...(p.anonymous ? {} : { uid }),
    removed: false,
    createdAt: serverTimestamp(),
    lastReplyAt: serverTimestamp(),
  });
  await batch.commit();
  await setDoc(mineRef(uid), { circlePosts: arrayUnion(`${cid}_${ref.id}`) }, { merge: true });
  return ref.id;
}

export async function replyInCircle(
  uid: string,
  username: string,
  cid: string,
  pid: string,
  body: string,
  cw: string,
  anonymous: boolean
) {
  const ref = doc(circleRepliesCol(cid, pid));
  await checkPace(uid, "comment");
  const batch = writeBatch(db);
  markPace(batch, uid, "comment");
  if (anonymous) batch.set(authorRef(`${cid}_${pid}_${ref.id}`), { uid });
  batch.set(ref, {
    body: body.trim().slice(0, LIMITS.reply),
    ...(cw.trim() ? { cw: cw.trim().slice(0, LIMITS.cw) } : {}),
    ...byline(username, anonymous),
    ...(anonymous ? {} : { uid }),
    removed: false,
    createdAt: serverTimestamp(),
  });
  batch.update(circlePostRef(cid, pid), { lastReplyAt: serverTimestamp() });
  await batch.commit();
  await setDoc(mineRef(uid), { circleReplies: arrayUnion(`${cid}_${pid}_${ref.id}`) }, { merge: true });
}

export const removeCirclePost = (cid: string, pid: string) =>
  updateDoc(circlePostRef(cid, pid), { removed: true, title: "(removed)", body: "", cw: "" });
export const removeCircleReply = (cid: string, pid: string, rid: string) =>
  updateDoc(doc(circleRepliesCol(cid, pid), rid), { removed: true, body: "", cw: "" });

// A lead hides (or un-hides) a post or comment that breaks the circle's guidelines.
export const setCirclePostHidden = (cid: string, pid: string, hidden: boolean) =>
  updateDoc(circlePostRef(cid, pid), { hidden });
export const setCircleReplyHidden = (cid: string, pid: string, rid: string, hidden: boolean) =>
  updateDoc(doc(circleRepliesCol(cid, pid), rid), { hidden });

// ---------- live data ----------

export function useCircle(cid?: string) {
  const [c, setC] = useState<Circle | null | undefined>(undefined);
  useEffect(() => {
    if (!cid) return;
    return onSnapshot(
      circleRef(cid),
      (snap) => setC(snap.exists() ? ({ id: snap.id, ...snap.data() } as Circle) : null),
      () => setC(null)
    );
  }, [cid]);
  return c;
}

// Is this person a member? (undefined while checking)
export function useMembership(cid?: string, uid?: string) {
  const [member, setMember] = useState<boolean | undefined>(undefined);
  useEffect(() => {
    if (!cid || !uid) return;
    return onSnapshot(
      doc(membersCol(cid), uid),
      (snap) => setMember(snap.exists()),
      () => setMember(false)
    );
  }, [cid, uid]);
  return member;
}
